import type { ReactNode } from "react";
import { action, computed, makeObservable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import type { QrScannerOverlayProps } from "./QrScannerOverlay";
import { OverlayEffects } from "@/components/shared/viewmodel/overlay-effects";
import {
  resolveQrScannerOverlayStatus,
  type QrScannerOverlayStatus,
  type QrScannerOverlayStatusTone,
} from "./viewmodel/status";

let overlayIdSeed = 0;

@injectable()
export class QrScannerOverlayViewModel extends ViewModel<
  unknown,
  QrScannerOverlayProps
> {
  private readonly uid = (overlayIdSeed += 1);

  private readonly keyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && this.isOpen) {
      this.onClose();
    }
  };

  private readonly overlayEffects = new OverlayEffects(this.keyDownHandler);

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get isOpen(): boolean {
    return this.viewProps.isOpen;
  }

  @computed
  get title(): string {
    return this.viewProps.title;
  }

  @computed
  get description(): string {
    return this.viewProps.description;
  }

  @computed
  get delay(): number | false {
    return this.viewProps.delay;
  }

  @computed
  get isResolving(): boolean {
    return this.viewProps.isResolving ?? false;
  }

  @computed
  get selectedPayload(): string {
    return this.viewProps.selectedPayload || "";
  }

  @computed
  get error(): string {
    return this.viewProps.error || "";
  }

  @computed
  get idleText(): string {
    return this.viewProps.idleText || "Наведите камеру на QR-код.";
  }

  @computed
  get cameraPreview(): ReactNode | null {
    return this.viewProps.cameraPreview || null;
  }

  @computed
  get selectedLabel(): string {
    return this.viewProps.selectedLabel || "Выбран QR";
  }

  @computed
  get statusText(): string {
    return this.status.text;
  }

  @computed
  get statusTone(): QrScannerOverlayStatusTone {
    return this.status.tone;
  }

  get titleId(): string {
    return `qr-scanner-overlay-title-${this.uid}`;
  }

  get descriptionId(): string {
    return `qr-scanner-overlay-description-${this.uid}`;
  }

  get statusId(): string {
    return `qr-scanner-overlay-status-${this.uid}`;
  }

  get ariaDescriptionId(): string {
    return `${this.descriptionId} ${this.statusId}`;
  }

  protected onViewMounted(): void {
    this.syncEffects();
  }

  protected onViewUpdated(): void {
    this.syncEffects();
  }

  protected onViewUnmounted(): void {
    this.overlayEffects.reset();
  }

  @action.bound
  onCloseClick(): void {
    this.onClose();
  }

  @action.bound
  onQrScan(value: string | null): void {
    void this.viewProps.onScan(value);
  }

  @action.bound
  onQrScanError(error: unknown): void {
    this.viewProps.onError(error);
  }

  private get onClose(): () => void {
    return this.viewProps.onClose;
  }

  private syncEffects(): void {
    this.overlayEffects.sync({
      keyDown: this.isOpen,
      scrollLock: this.isOpen,
    });
  }

  private get status(): QrScannerOverlayStatus {
    return resolveQrScannerOverlayStatus({
      isResolving: this.isResolving,
      error: this.error,
      selectedPayload: this.selectedPayload,
      selectedLabel: this.selectedLabel,
      idleText: this.idleText,
    });
  }
}
